import React, { useState, useEffect } from 'react';

const LandingPage = ({ onNavigate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Request notification permission when landing page loads
  useEffect(() => {
    console.log('🔔 Landing page loaded - starting notification permission check');

    const requestNotificationPermission = async () => {
      try {
        console.log('🔔 Step 1: Checking browser support...');

        // Check if notifications are supported
        if (!('Notification' in window)) {
          console.log('❌ This browser does not support notifications');
          return;
        }
        console.log('✅ Notifications are supported');

        // Check if service worker is supported
        if (!('serviceWorker' in navigator)) {
          console.log('❌ Service workers are not supported');
          return;
        }
        console.log('✅ Service workers are supported');

        // Check current permission status
        console.log('🔔 Step 2: Current permission status:', Notification.permission);

        if (Notification.permission === 'default') {
          console.log('🔔 Step 3: Permission is default, will request in 2 seconds...');

          // Wait a bit before asking for permission (better UX)
          setTimeout(async () => {
            console.log('🔔 Step 4: Requesting notification permission NOW...');

            const permission = await Notification.requestPermission();
            console.log('🔔 Step 5: Permission result:', permission);

            if (permission === 'granted') {
              console.log('✅ Notification permission GRANTED!');

              // Import and initialize push notifications
              try {
                console.log('🔔 Step 6: Importing push notification utilities...');
                const { initializePushNotifications } = await import('../utils/pushNotifications');

                console.log('🔔 Step 7: Initializing push notifications...');
                const result = await initializePushNotifications();

                if (result.success) {
                  console.log('✅ Push notifications initialized successfully!');
                } else {
                  console.log('❌ Failed to initialize push notifications:', result.message);
                }
              } catch (error) {
                console.error('❌ Error initializing push notifications:', error);
              }
            } else if (permission === 'denied') {
              console.log('❌ Notification permission DENIED by user');
            } else {
              console.log('⚠️ Notification permission dismissed (neither granted nor denied)');
            }
          }, 2000); // Wait 2 seconds after page load

        } else if (Notification.permission === 'granted') {
          console.log('✅ Permission already granted, re-initializing...');

          // Permission already granted, initialize push notifications
          try {
            const { initializePushNotifications } = await import('../utils/pushNotifications');
            const result = await initializePushNotifications();

            if (result.success) {
              console.log('✅ Push notifications re-initialized successfully');
            }
          } catch (error) {
            console.error('❌ Error re-initializing push notifications:', error);
          }
        } else if (Notification.permission === 'denied') {
          console.log('❌ Notification permission was previously DENIED. User needs to reset in browser settings.');
        }
      } catch (error) {
        console.error('❌ Error in notification permission flow:', error);
      }
    };

    requestNotificationPermission();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Function to handle navigation
  const handleNavigation = (page) => {
    if (page === 'home') {
      // For home, we can scroll to top or do nothing since we're already on home
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // For other pages, use the navigation function
      onNavigate(page);
    }
    setIsMenuOpen(false); // Close mobile menu after navigation
  };

  // Function to handle Learn More redirects
  const handleLearnMore = (program) => {
    const redirectUrl = "https://www.example.com";
    window.open(redirectUrl, '_blank');
  };

  // Function to handle footer link redirects
  const handleFooterLinkClick = (linkType) => {
    if (linkType === 'privacypolicy') {
      onNavigate('privacypolicy');
      return;
    }
    const redirectUrl = "https://www.example.com";
    window.open(redirectUrl, '_blank');
  };

  // Function to handle social media links
  const handleSocialClick = (platform) => {
    const urls = {
      twitter: 'https://twitter.com/your-university',
      facebook: 'https://facebook.com/your-university',
      pinterest: 'https://pinterest.com/your-university',
      linkedin: 'https://linkedin.com/company/your-university'
    };
    window.open(urls[platform], '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#ffffff',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 0'
          }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleNavigation('home')}>
              <img
                src="/logo.png"
                alt="Swami Vivekananda University Logo"
                style={{ width: '48px', height: '48px', objectFit: 'contain' }}
              />
              <div style={{ marginLeft: '12px' }}>
                <h1 style={{
                  fontSize: isMobile ? '16px' : '20px',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  margin: 0
                }}>Swami Vivekananda</h1>
                <p style={{
                  fontSize: '14px',
                  color: '#0f766e',
                  margin: 0
                }}>University</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            {!isMobile && (
              <nav style={{
                display: 'flex',
                gap: '2rem'
              }}>
                <button onClick={() => handleNavigation('home')} style={{
                  color: '#374151',
                  fontWeight: '500',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.3s',
                  position: 'relative',
                  padding: '0.5rem 0',
                  fontSize: '16px'
                }}
                  onMouseEnter={(e) => e.target.style.color = '#0f766e'}
                  onMouseLeave={(e) => e.target.style.color = '#374151'}>
                  Home
                </button>
                <button onClick={() => handleNavigation('about')} style={{
                  color: '#374151',
                  fontWeight: '500',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.3s',
                  position: 'relative',
                  padding: '0.5rem 0',
                  fontSize: '16px'
                }}
                  onMouseEnter={(e) => e.target.style.color = '#0f766e'}
                  onMouseLeave={(e) => e.target.style.color = '#374151'}>
                  About
                </button>
                <button onClick={() => handleNavigation('contact')} style={{
                  color: '#374151',
                  fontWeight: '500',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.3s',
                  position: 'relative',
                  padding: '0.5rem 0',
                  fontSize: '16px'
                }}
                  onMouseEnter={(e) => e.target.style.color = '#0f766e'}
                  onMouseLeave={(e) => e.target.style.color = '#374151'}>
                  Contact
                </button>
              </nav>
            )}

            {/* Desktop Login Button */}
            {!isMobile && (
              <button onClick={() => handleNavigation('login')} style={{
                backgroundColor: '#0f766e',
                color: 'white',
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)'
              }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0d9488';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(15, 118, 110, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0f766e';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(15, 118, 110, 0.2)';
                }}>
                Portal Login
              </button>
            )}

            {/* Mobile Hamburger Menu */}
            {isMobile && (
              <button
                onClick={toggleMenu}
                style={{
                  width: '40px',
                  height: '40px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  zIndex: 1001
                }}
              >
                <div style={{
                  width: '25px',
                  height: '3px',
                  backgroundColor: '#374151',
                  borderRadius: '3px',
                  position: 'absolute',
                  top: isMenuOpen ? '50%' : '30%',
                  left: '50%',
                  transform: `translate(-50%, -50%) ${isMenuOpen ? 'rotate(45deg)' : 'rotate(0)'}`,
                  transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                }}></div>
                <div style={{
                  width: '25px',
                  height: '3px',
                  backgroundColor: '#374151',
                  borderRadius: '3px',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  opacity: isMenuOpen ? 0 : 1,
                  transition: 'all 0.3s ease'
                }}></div>
                <div style={{
                  width: '25px',
                  height: '3px',
                  backgroundColor: '#374151',
                  borderRadius: '3px',
                  position: 'absolute',
                  top: isMenuOpen ? '50%' : '70%',
                  left: '50%',
                  transform: `translate(-50%, -50%) ${isMenuOpen ? 'rotate(-45deg)' : 'rotate(0)'}`,
                  transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                }}></div>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobile && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: isMenuOpen ? '20vw' : '0',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 998,
            opacity: isMenuOpen ? 1 : 0,
            visibility: isMenuOpen ? 'visible' : 'hidden',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(3px)'
          }} onClick={toggleMenu}></div>
        )}

        {/* Mobile Slide Menu */}
        {isMobile && (
          <div style={{
            position: 'fixed',
            top: 0,
            right: isMenuOpen ? '0' : '-100%',
            width: '80vw',
            maxWidth: '300px',
            height: '100vh',
            backgroundColor: '#ffffff',
            boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.2)',
            transition: 'right 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            zIndex: 999,
            padding: '6rem 2rem 2rem 2rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}>
            {/* Menu Items */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              {[
                { name: 'Home', page: 'home' },
                { name: 'About', page: 'about' },
                { name: 'Contact', page: 'contact' }
              ].map((item, index) => (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.page)}
                  style={{
                    color: '#374151',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    display: 'block',
                    position: 'relative',
                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(50px)',
                    opacity: isMenuOpen ? 1 : 0,
                    transitionDelay: `${index * 0.1 + 0.2}s`,
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#0f766e';
                    e.target.style.backgroundColor = '#f0fdfa';
                    e.target.style.transform = 'translateX(10px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#374151';
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  {item.name}
                  <span style={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '20px',
                    opacity: 0.5
                  }}>→</span>
                </button>
              ))}

              {/* Portal Login Button with Green Container */}
              <button
                onClick={() => handleNavigation('login')}
                style={{
                  color: 'white',
                  fontWeight: '600',
                  backgroundColor: '#0f766e',
                  border: 'none',
                  fontSize: '18px',
                  padding: '16px 20px',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  display: 'block',
                  position: 'relative',
                  transform: isMenuOpen ? 'translateX(0)' : 'translateX(50px)',
                  opacity: isMenuOpen ? 1 : 0,
                  transitionDelay: `${3 * 0.1 + 0.2}s`,
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  boxShadow: '0 4px 12px rgba(15, 118, 110, 0.3)',
                  marginTop: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0d9488';
                  e.target.style.transform = 'translateX(10px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(15, 118, 110, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0f766e';
                  e.target.style.transform = 'translateX(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(15, 118, 110, 0.3)';
                }}
              >
                Portal Login
                <span style={{
                  position: 'absolute',
                  right: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '20px',
                  opacity: 0.8
                }}>→</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" style={{
        background: 'linear-gradient(135deg, #f0fdfa 0%, #dcfce7 50%, #e6fffa 100%)',
        padding: isMobile ? '4rem 0' : '6rem 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(45deg, rgba(15, 118, 110, 0.1), rgba(5, 150, 105, 0.1))',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }}></div>

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem',
          position: 'relative'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: isMobile ? '2.5rem' : '4rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1.5rem',
              lineHeight: '1.2'
            }}>
              Welcome to <span style={{
                color: '#0f766e',
                background: 'linear-gradient(135deg, #0f766e, #059669)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Swami Vivekananda</span> University
            </h1>
            <p style={{
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              color: '#374151',
              marginBottom: '3rem',
              maxWidth: '800px',
              margin: '0 auto 3rem auto',
              lineHeight: '1.6'
            }}>
              Dedicated to the pursuit of knowledge and excellence, providing quality education to our students since 2019. Experience exceptional learning in the heart of West Bengal.
            </p>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <button style={{
                backgroundColor: '#0f766e',
                color: 'white',
                padding: '14px 32px',
                borderRadius: '12px',
                border: 'none',
                fontWeight: '600',
                fontSize: '18px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '300px' : 'none',
                boxShadow: '0 4px 16px rgba(15, 118, 110, 0.3)'
              }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0d9488';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 24px rgba(15, 118, 110, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0f766e';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(15, 118, 110, 0.3)';
                }}>
                Explore Programs
              </button>
              <button style={{
                border: '2px solid #0f766e',
                color: '#0f766e',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                padding: '14px 32px',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '18px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '300px' : 'none',
                backdropFilter: 'blur(10px)'
              }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0f766e';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                  e.target.style.color = '#0f766e';
                  e.target.style.transform = 'translateY(0)';
                }}>
                Virtual Tour
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={{
        padding: isMobile ? '4rem 0' : '6rem 0',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1rem'
            }}>About Our University</h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Built on the thoughts and perspectives of Swami Vivekananda, we facilitate and nurture the growth and development of our students to ensure they reach their full potential.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              { icon: '🎓', title: '113+ Courses', desc: 'Comprehensive programs across nine faculties spanning undergraduate, postgraduate, diploma, and doctoral levels.' },
              { icon: '🏛️', title: '12-Acre Campus', desc: 'Campus surrounded by greenery and water bodies with modern amenities including gym, cafeteria, open-air theatre, auditorium, and library.' },
              { icon: '🏆', title: 'UGC Recognized', desc: 'University Grants Commission (UGC) recognized and approved by the Department of Higher Education, West Bengal.' }
            ].map((item, index) => (
              <div key={index} style={{
                textAlign: 'center',
                padding: '2rem',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ccfbf1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem auto'
                }}>
                  <span style={{ fontSize: '36px' }}>{item.icon}</span>
                </div>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '12px'
                }}>{item.title}</h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" style={{
        padding: isMobile ? '4rem 0' : '6rem 0',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1rem'
            }}>Academic Programs</h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Courses ranging from engineering and management to humanities, designed to provide students with skills and expertise required to excel in their chosen fields.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {[
              { title: "Engineering & Technology", courses: "B.Tech, M.Tech", specializations: "CSE, ECE, EE, ME, Civil", emoji: "⚙️" },
              { title: "Management Studies", courses: "BBA, MBA", specializations: "Industry-aligned curriculum", emoji: "💼" },
              { title: "Life Sciences", courses: "B.Sc, M.Sc", specializations: "Biotechnology, Agriculture, Medical Lab Tech", emoji: "🔬" },
              { title: "Health Sciences", courses: "B.Sc Medical", specializations: "Physiotherapy, Optometry, Radiology", emoji: "🏥" }
            ].map((program, index) => (
              <div key={index} style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                padding: '2rem',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(15, 118, 110, 0.1)'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
                  e.currentTarget.style.borderColor = '#0f766e';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.1)';
                }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ccfbf1',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                  margin: '0 auto 1.5rem auto',
                  boxShadow: '0 4px 12px rgba(15, 118, 110, 0.15)'
                }}>
                  <span style={{
                    fontSize: '36px',
                    lineHeight: '1',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>{program.emoji}</span>
                </div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '8px'
                }}>{program.title}</h3>
                <p style={{
                  color: '#0f766e',
                  marginBottom: '8px',
                  fontWeight: '600'
                }}>{program.courses}</p>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  marginBottom: '1.5rem'
                }}>{program.specializations}</p>
                <button
                  onClick={() => handleLearnMore(program.title)}
                  style={{
                    color: '#0f766e',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateX(0)';
                  }}
                >
                  Learn More →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Placement Section */}
      <section style={{
        padding: isMobile ? '4rem 0' : '6rem 0',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1rem'
            }}>100% Placement Assurance</h2>
            <p style={{
              fontSize: isMobile ? '18px' : '20px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Our training and placement cell ensures 100% placement of students through guidance from their placement and entrepreneurship cell.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            marginBottom: '4rem'
          }}>
            {[
              { stat: '₹25 LPA', desc: 'Highest Package Offered' },
              { stat: '100%', desc: 'Placement Assurance' },
              { stat: '200+', desc: 'Recruiting Companies' }
            ].map((item, index) => (
              <div key={index} style={{
                textAlign: 'center',
                padding: '2rem',
                borderRadius: '16px',
                backgroundColor: '#f8fafc',
                border: '2px solid #e2e8f0',
                transition: 'all 0.3s ease'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#0f766e';
                  e.currentTarget.style.backgroundColor = '#f0fdfa';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                <div style={{
                  fontSize: isMobile ? '2.5rem' : '3rem',
                  fontWeight: 'bold',
                  color: '#0f766e',
                  marginBottom: '8px'
                }}>{item.stat}</div>
                <p style={{ color: '#6b7280', fontWeight: '500' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <h3 style={{
              fontSize: isMobile ? '1.5rem' : '2rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '2rem'
            }}>Top Recruiters</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem'
            }}>
              {["Wipro", "PwC", "Panacea Smart Solutions", "TCS", "Infosys", "Cognizant", "HCL", "Tech Mahindra"].map((company, index) => (
                <div key={index} style={{
                  backgroundColor: '#f3f4f6',
                  borderRadius: '12px',
                  padding: '1.5rem 1rem',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e5e7eb'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f766e';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.color = '#374151';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}>
                  {company}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{
        padding: isMobile ? '4rem 0' : '6rem 0',
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
        color: 'white'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '4rem'
          }}>
            <div>
              <h2 style={{
                fontSize: isMobile ? '2.5rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem'
              }}>Get in Touch</h2>
              <p style={{
                fontSize: isMobile ? '18px' : '20px',
                color: '#d1d5db',
                marginBottom: '3rem',
                lineHeight: '1.6'
              }}>
                Have questions about admissions, programs, or campus life? We're here to help you take the next step in your educational journey.
              </p>

              <div style={{ marginBottom: '3rem' }}>
                {[
                  { icon: '📍', text: 'Telinipara, Barasat, Barrackpore Road, Bara Kanthalia, West Bengal 700121', link: 'https://maps.app.goo.gl/qX5SvAa7uWZaPdxi7' },
                  { icon: '📞', text: '+91-33-2592-0001', link: 'tel:+91-33-2592-0001' },
                  { icon: '✉️', text: 'info@swamivivekanandauniversity.ac.in', link: 'mailto:info@swamivivekanandauniversity.ac.in' }
                ].map((contact, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(15, 118, 110, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}>
                    <span style={{
                      color: '#5eead4',
                      marginRight: '16px',
                      fontSize: '24px',
                      minWidth: '24px'
                    }}>{contact.icon}</span>
                    <a
                      href={contact.link}
                      target={contact.link.startsWith('http') ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                      style={{
                        color: '#d1d5db',
                        textDecoration: 'none',
                        transition: 'color 0.3s ease',
                        lineHeight: '1.5'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#5eead4'}
                      onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                    >
                      {contact.text}
                    </a>
                  </div>
                ))}
              </div>

              {/* Social Media Icons */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                {[
                  { platform: 'twitter', path: 'M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z' },
                  { platform: 'facebook', path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                  { platform: 'linkedin', path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' }
                ].map(social => (
                  <button
                    key={social.platform}
                    onClick={() => handleSocialClick(social.platform)}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      border: '2px solid #374151',
                      backgroundColor: 'transparent',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#5eead4';
                      e.target.style.backgroundColor = '#5eead4';
                      e.target.style.color = '#1f2937';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#374151';
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#9ca3af';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d={social.path} />
                    </svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Google Maps Section */}
            <div style={{
              backgroundColor: 'rgba(55, 65, 81, 0.5)',
              borderRadius: '16px',
              padding: '2rem',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem'
              }}>Our Location</h3>
              <div style={{
                width: '100%',
                height: '400px',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '2px solid rgba(94, 234, 212, 0.2)'
              }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.1692541877833!2d88.39326367600272!3d22.759099226107793!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f899aa114efb0f%3A0xf345029031347802!2sSwami%20Vivekananda%20University!5e0!3m2!1sen!2sin!4v1753770500247!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Swami Vivekananda University Location"
                ></iframe>
              </div>
              <div style={{ marginTop: '1.5rem', fontSize: '14px', color: '#d1d5db' }}>
                <p style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>📍</span>
                  Telinipara, Barasat, Barrackpore Road
                </p>
                <p style={{ margin: 0, paddingLeft: '24px' }}>Bara Kanthalia, West Bengal 700121</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#111827',
        color: '#d1d5db',
        padding: '4rem 0 2rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '3rem',
            marginBottom: '3rem',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.5rem',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                <img
                  src="/logo.png"
                  alt="SVU Logo"
                  style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                />
                <div style={{ marginLeft: '12px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: 'white',
                    margin: 0
                  }}>Swami Vivekananda</h3>
                  <p style={{ fontSize: '14px', margin: 0 }}>University</p>
                </div>
              </div>
              <p style={{
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                Empowering minds, building futures through quality education and holistic development since 2019.
              </p>
            </div>

            {[
              {
                title: 'Quick Links',
                links: ['Admissions', 'Academic Calendar', 'WBJEE Counseling', 'Scholarships']
              },
              {
                title: 'Resources',
                links: ['Digital Library', 'Research Centers', 'Career Services', 'Alumni Network']
              },
              {
                title: 'Campus Life',
                links: ['Events', 'Clubs & Societies', 'Sports Complex', 'Hostel Life', 'Privacy Policy']
              }
            ].map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'white',
                  marginBottom: '1.5rem',
                  textAlign: isMobile ? 'center' : 'left'
                }}>{section.title}</h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex} style={{ marginBottom: '12px' }}>
                      <button
                        onClick={() => handleFooterLinkClick(link.toLowerCase().replace(' & ', '').replace(' ', ''))}
                        style={{
                          color: '#d1d5db',
                          background: 'none',
                          border: 'none',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          padding: '4px 0',
                          textAlign: isMobile ? 'center' : 'left',
                          width: '100%'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#5eead4';
                          e.target.style.transform = 'translateX(5px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#d1d5db';
                          e.target.style.transform = 'translateX(0)';
                        }}
                      >{link}</button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{
            borderTop: '1px solid #374151',
            paddingTop: '2rem',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', margin: 0, opacity: 0.8 }}>
              &copy; 2025 Swami Vivekananda University. All rights reserved. | UGC Recognized Private University
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;